import React, { useState, useRef, useMemo } from 'react';
import axios from 'axios';

// ─── Constantes partagées ─────────────────────────────────────────────────────
export const CONDITIONS = ['Non', 'Actualisable', 'Révisable', 'Actualisable et Révisable'];
export const ETATS = ['Non répondu', 'En cours', 'Perdu', 'Gagné', 'Terminé', 'Declaration sans suite'];

export const ETAT_BADGE = {
  'Non répondu': { background: '#e2e3e5', color: '#383d41' },
  'En cours':    { background: '#d1ecf1', color: '#0c5460' },
  'Perdu':       { background: '#f8d7da', color: '#721c24' },
  'Gagné':       { background: '#d4edda', color: '#155724' },
  'Terminé':     { background: '#e2d5f1', color: '#4a235a' },
  'Declaration sans suite': { background: '#fde9d0', color: '#9c4221' },
};

export function getEtat(m) {
  if (m.etat) return m.etat;
  if (m.retenu) return 'Gagné';
  return 'En cours';
}

export function formatMontant(val) {
  if (!val && val !== 0) return '';
  const n = parseFloat(String(val).replace(',', '.'));
  if (isNaN(n)) return val;
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function parseMontant(str) {
  if (!str) return null;
  return parseFloat(String(str).replace(/\s/g, '').replace(',', '.')) || null;
}

export function toInputDate(val) {
  if (!val) return '';
  // Composantes locales (toISOString décale d'un jour les dates à minuit local)
  const d = new Date(val);
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  dialog: {
    background: '#fff', borderRadius: '8px', width: '680px', maxWidth: '95vw',
    maxHeight: '90vh', display: 'flex', flexDirection: 'column',
    boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
  },
  dialogHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '1.25rem 1.5rem', background: '#2c3e50', borderRadius: '8px 8px 0 0',
  },
  dialogTitle: { color: '#fff', fontSize: '1.1rem', fontWeight: 600, margin: 0 },
  closeBtn: { background: 'none', border: 'none', color: '#fff', fontSize: '1.5rem', cursor: 'pointer' },
  dialogBody: { padding: '1.5rem', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem' },
  dialogFooter: {
    padding: '1rem 1.5rem', borderTop: '1px solid #ecf0f1',
    display: 'flex', justifyContent: 'flex-end', gap: '0.75rem',
    background: '#f8f9fa', borderRadius: '0 0 8px 8px',
  },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
  grid3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' },
  sectionTitle: {
    fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.08em', color: '#7f8c8d',
    margin: '0.5rem 0 0.25rem', paddingBottom: '0.35rem',
    borderBottom: '1px solid #ecf0f1',
  },
  label: { display: 'block', marginBottom: '0.3rem', fontSize: '0.875rem', fontWeight: 500, color: '#2c3e50' },
  required: { color: '#e74c3c', marginLeft: '2px' },
  input: {
    width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #ddd',
    borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box',
  },
  select: {
    width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #ddd',
    borderRadius: '4px', fontSize: '0.9rem', background: '#fff', boxSizing: 'border-box',
  },
  error: {
    background: '#f8d7da', color: '#721c24', padding: '0.7rem 1rem',
    borderRadius: '4px', fontSize: '0.875rem', borderLeft: '4px solid #e74c3c',
  },
};

const EMPTY_FORM = {
  client_codification: '', reference_client: '', dlro: '',
  intitule_marche: '', lot: '', montant: '',
  conditions_financieres: 'Non', etat: 'En cours', notification: '',
  heure_depot: '',
  entreprise_codification: '',
};

function Field({ label, required, children }) {
  return (
    <div>
      <label style={S.label}>{label}{required && <span style={S.required}>*</span>}</label>
      {children}
    </div>
  );
}

// ─── Modale création / modification / détail d'un marché ─────────────────────
// marche       : objet marché à éditer (null = création)
// initialForm  : formulaire pré-rempli (duplication) — utilisé seulement si marche est null
// onSaved      : appelé après enregistrement réussi (refetch côté parent)
// onDelete     : si fourni, affiche le bouton Supprimer (le parent gère confirm + delete)
function MarcheFormModal({ user, clients, entreprises, marche, initialForm, initialViewOnly = false, onClose, onSaved, onDelete }) {
  const isAdmin = user && user.role === 'Admin';

  const initial = useMemo(() => {
    if (marche) {
      return {
        client_codification:  marche.client_codification || '',
        reference_client:     marche.reference_client || '',
        dlro:                 toInputDate(marche.dlro),
        intitule_marche:      marche.intitule_marche || '',
        lot:                  marche.lot || '',
        montant:              marche.montant || '',
        conditions_financieres: marche.conditions_financieres || 'Non',
        etat:                 getEtat(marche),
        notification:         toInputDate(marche.notification),
        heure_depot:          marche.heure_depot || '',
        entreprise_codification: marche.entreprise_codification || '',
      };
    }
    if (initialForm) return { ...EMPTY_FORM, ...initialForm };
    return { ...EMPTY_FORM, entreprise_codification: isAdmin ? '' : (user?.entreprise_codification || '') };
  }, []); // eslint-disable-line

  const [viewOnly, setViewOnly] = useState(initialViewOnly);
  const [form, setForm] = useState(initial);
  const [montantDisplay, setMontantDisplay] = useState(initial.montant ? formatMontant(initial.montant) : '');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [duplicateWarning, setDuplicateWarning] = useState('');
  const duplicateTimerRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const updated = { ...prev, [name]: value };
      // Vérifier doublons quand client ou ref_client change
      if (name === 'client_codification' || name === 'reference_client') {
        clearTimeout(duplicateTimerRef.current);
        duplicateTimerRef.current = setTimeout(() => {
          checkDuplicate(updated.client_codification, updated.reference_client);
        }, 500);
      }
      return updated;
    });
  };

  const handleMontantChange = (e) => {
    setMontantDisplay(e.target.value);
    setForm(prev => ({ ...prev, montant: e.target.value }));
  };

  const handleMontantBlur = () => {
    const parsed = parseMontant(montantDisplay);
    if (parsed !== null) {
      setMontantDisplay(formatMontant(parsed));
      setForm(prev => ({ ...prev, montant: parsed }));
    }
  };

  const handleMontantFocus = () => {
    if (form.montant) {
      setMontantDisplay(String(form.montant).replace('.', ','));
    }
  };

  // Vérification doublons (client + ref_client)
  const checkDuplicate = async (clientCodif, refClient) => {
    if (!clientCodif || !refClient) { setDuplicateWarning(''); return; }
    try {
      const r = await axios.get('/api/marches/check-duplicate', {
        params: {
          client_codification: clientCodif,
          reference_client: refClient,
          exclude_id: marche?.reference_interne || ''
        }
      });
      if (r.data.duplicate) {
        const match = r.data.matches[0];
        setDuplicateWarning(`Attention : un marché existe déjà pour ce client avec cette référence (${match.codification} — ${match.intitule_marche || ''}).`);
      } else {
        setDuplicateWarning('');
      }
    } catch(e) { /* ignore */ }
  };

  const handleSubmit = async () => {
    setFormError('');
    if (!form.intitule_marche.trim()) {
      setFormError("L'intitulé du marché est obligatoire."); return;
    }
    if (!form.dlro) {
      setFormError("La date de DLRO est obligatoire."); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        montant: parseMontant(form.montant),
        heure_depot: form.heure_depot || null,
      };
      if (marche) {
        await axios.put('/api/marches/' + marche.reference_interne, payload);
      } else {
        await axios.post('/api/marches', payload);
      }
      if (onSaved) await onSaved();
      onClose();
    } catch(err) {
      setFormError(err.response?.data?.details || err.response?.data?.error || "Erreur lors de l'enregistrement.");
    } finally { setSaving(false); }
  };

  const closeModal = () => { if (!saving) onClose(); };

  return (
    <div style={S.overlay} onClick={closeModal}>
      <div style={S.dialog} onClick={e => e.stopPropagation()}>

        <div style={{ ...S.dialogHeader, background: viewOnly ? '#34495e' : '#2c3e50' }}>
          <h2 style={S.dialogTitle}>
            {viewOnly
              ? 'Détail — ' + (marche?.codification || 'Marché n°' + marche?.reference_interne)
              : marche ? 'Modifier — ' + (marche.codification || 'Marché n°' + marche.reference_interne) : 'Nouveau marché'}
          </h2>
          <button style={S.closeBtn} onClick={closeModal}>×</button>
        </div>

        <div style={S.dialogBody}>
          {formError && <div style={S.error}>{formError}</div>}
          {duplicateWarning && (
            <div style={{ background: '#fff3cd', color: '#856404', padding: '0.7rem 1rem',
              borderRadius: '4px', fontSize: '0.875rem', borderLeft: '4px solid #ffc107' }}>
              {duplicateWarning}
            </div>
          )}

          {/* Entreprise (Admin seulement) */}
          {isAdmin && (
            <>
              <div style={S.sectionTitle}>Entité</div>
              <Field label="Entité" required>
                <select style={S.select} name="entreprise_codification"
                  value={form.entreprise_codification} onChange={handleChange} disabled={viewOnly}>
                  <option value="">— Sélectionner une entité —</option>
                  {entreprises.map(e => (
                    <option key={e.codification} value={e.codification}>
                      {e.nom} ({e.codification})
                    </option>
                  ))}
                </select>
              </Field>
            </>
          )}

          {/* Identification */}
          <div style={S.sectionTitle}>Identification</div>
          <div style={S.grid2}>
            <Field label="Client">
              <select style={S.select} name="client_codification"
                value={form.client_codification} onChange={handleChange} disabled={viewOnly}>
                <option value="">— Sélectionner un client —</option>
                {clients.map(c => (
                  <option key={c.codification} value={c.codification}>
                    {c.nom} ({c.codification})
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Référence client">
              <input style={S.input} name="reference_client"
                value={form.reference_client} onChange={handleChange}
                placeholder="Ex : AO-2024-001" readOnly={viewOnly} />
            </Field>
          </div>

          <Field label="Intitulé du marché" required={!viewOnly}>
            <input style={S.input} name="intitule_marche"
              value={form.intitule_marche} onChange={handleChange}
              placeholder="Désignation complète du marché" readOnly={viewOnly} />
          </Field>

          <Field label="Lot">
            <input style={S.input} name="lot" value={form.lot}
              onChange={handleChange} placeholder="Ex : Lot 3 — Électricité" readOnly={viewOnly} />
          </Field>

          {/* Financier */}
          <div style={S.sectionTitle}>Modalité Évolution Prix</div>
          <div style={S.grid2}>
            <Field label="Montant (€)">
              <input style={{ ...S.input, textAlign: 'right' }}
                name="montant"
                value={montantDisplay}
                onChange={handleMontantChange}
                onBlur={handleMontantBlur}
                onFocus={handleMontantFocus}
                placeholder="0,00"
                readOnly={viewOnly}
              />
            </Field>
            <Field label="Modalité Évolution Prix">
              <select style={S.select} name="conditions_financieres"
                value={form.conditions_financieres} onChange={handleChange} disabled={viewOnly}>
                {CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>

          {/* Calendrier & État */}
          <div style={S.sectionTitle}>Calendrier & État</div>
          <div style={S.grid3}>
            <Field label="DLRO" required={!viewOnly}>
              <input style={S.input} type="date" name="dlro"
                value={form.dlro} onChange={handleChange} readOnly={viewOnly} />
            </Field>
            <Field label="Heure de dépôt">
              <input style={S.input} type="time" name="heure_depot"
                value={form.heure_depot} onChange={handleChange} readOnly={viewOnly} />
            </Field>
            <Field label="Date de notification">
              <input style={S.input} type="date" name="notification"
                value={form.notification} onChange={handleChange} readOnly={viewOnly} />
            </Field>
          </div>
          <div style={{ ...S.grid3, marginTop: '1rem' }}>
            <Field label="État">
              <select style={S.select} name="etat"
                value={form.etat} onChange={handleChange} disabled={viewOnly}>
                {ETATS.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            </Field>
          </div>
        </div>

        <div style={S.dialogFooter}>
          {viewOnly ? (
            <>
              <button className="btn btn-secondary" onClick={closeModal}>Fermer</button>
              <button className="btn btn-primary" onClick={() => setViewOnly(false)}>Modifier</button>
            </>
          ) : (
            <>
              {marche && onDelete && (
                <button className="btn btn-danger"
                  style={{ marginRight: 'auto' }}
                  onClick={() => { onClose(); onDelete(marche.reference_interne); }}
                  disabled={saving}>
                  Supprimer
                </button>
              )}
              <button className="btn btn-secondary" onClick={closeModal} disabled={saving}>
                Annuler
              </button>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
                {saving ? 'Enregistrement…' : marche ? 'Enregistrer' : 'Créer'}
              </button>
            </>
          )}
        </div>

      </div>
    </div>
  );
}

export default MarcheFormModal;
